/**
 * Cliente HTTP para la API de autenticación y gestión de usuarios.
 *
 * Todas las peticiones envían la cookie de sesión (`cec.sid`) automáticamente
 * porque el frontend y la API viven en el mismo origen (Vite proxy en dev,
 * IIS reverse proxy en producción).
 */

export type UserRole = "admin" | "contable" | "consulta";

export type SessionUser = {
  username: string;
  displayName: string;
  role: UserRole;
  mustChangePassword: boolean;
  /** Acceso al módulo Cartera y vencimientos (admin siempre; resto si un admin lo concede). */
  carteraAccess?: boolean;
  /** Apartados a los que tiene acceso (ya resueltos por el backend). */
  sections?: string[];
  lastLoginAt?: string | null;
};

export type SectionDef = { id: string; label: string; defaultRoles: UserRole[] };

export type UserRecord = SessionUser & {
  active: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  /** true si un admin personalizó la lista de apartados (no es la del rol). */
  customSections?: boolean;
};

async function jsonOrThrow(res: Response): Promise<unknown> {
  const text = await res.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = text;
  }
  if (!res.ok) {
    const err = new Error(
      typeof body === "object" && body && "message" in body && typeof (body as Record<string, unknown>)["message"] === "string"
        ? ((body as Record<string, unknown>)["message"] as string)
        : typeof body === "object" && body && "error" in body && typeof (body as Record<string, unknown>)["error"] === "string"
          ? ((body as Record<string, unknown>)["error"] as string)
          : `HTTP ${res.status}`,
    ) as Error & { status: number; code?: string };
    err.status = res.status;
    if (typeof body === "object" && body && "error" in body) {
      err.code = String((body as Record<string, unknown>)["error"]);
    }
    throw err;
  }
  return body;
}

export async function login(username: string, password: string): Promise<SessionUser> {
  const res = await fetch("/api/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  const body = (await jsonOrThrow(res)) as { user: SessionUser };
  return body.user;
}

export async function logout(): Promise<void> {
  await fetch("/api/auth/logout", { method: "POST" });
}

/** Devuelve el usuario en sesión, o null si no hay sesión activa. */
export async function fetchMe(): Promise<SessionUser | null> {
  const res = await fetch("/api/auth/me");
  if (res.status === 401) return null;
  const body = (await jsonOrThrow(res)) as { user: SessionUser };
  return body.user;
}

export async function changeMyPassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  const res = await fetch("/api/auth/change-password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  await jsonOrThrow(res);
}

// --- Gestión de usuarios (admin) ---

export async function fetchUsers(): Promise<UserRecord[]> {
  const res = await fetch("/api/users");
  const body = (await jsonOrThrow(res)) as { count: number; items: UserRecord[] };
  return body.items;
}

export async function fetchSections(): Promise<SectionDef[]> {
  const res = await fetch("/api/users/sections");
  const body = (await jsonOrThrow(res)) as { items: SectionDef[] };
  return body.items;
}

export async function createUserApi(input: {
  username: string;
  displayName: string;
  password: string;
  role: UserRole;
  carteraAccess?: boolean;
  sections?: string[];
}): Promise<UserRecord> {
  const res = await fetch("/api/users", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const body = (await jsonOrThrow(res)) as { user: UserRecord };
  return body.user;
}

export async function patchUserApi(
  username: string,
  patch: { displayName?: string; role?: UserRole; active?: boolean; carteraAccess?: boolean; sections?: string[] | null },
): Promise<UserRecord> {
  const res = await fetch(`/api/users/${encodeURIComponent(username)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  const body = (await jsonOrThrow(res)) as { user: UserRecord };
  return body.user;
}

export async function resetUserPasswordApi(username: string, newPassword: string): Promise<UserRecord> {
  const res = await fetch(`/api/users/${encodeURIComponent(username)}/reset-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ newPassword }),
  });
  const body = (await jsonOrThrow(res)) as { user: UserRecord };
  return body.user;
}

export async function deleteUserApi(username: string): Promise<void> {
  const res = await fetch(`/api/users/${encodeURIComponent(username)}`, {
    method: "DELETE",
  });
  await jsonOrThrow(res);
}
